;(() => {
  // =========================
  // Config — adjust as needed
  // =========================
  const TARIFF_FEE_HANDLE = 'tariff-fee';       // Hidden product handle for the fee
  const ELIGIBLE_TAG = 'tariff_item_default';   // Tag (lowercased) that marks products to count

  // ---- Debug switch (set to true to re-enable debug logging)
  const CSP_DEBUG = false;
  const CSP_LOG = {
    debug: (...a) => { if (CSP_DEBUG) console.debug(...a); },
    info:  (...a) => { if (CSP_DEBUG) console.info(...a);  },
    log:   (...a) => { if (CSP_DEBUG) console.log(...a);   },
    warn:  (...a) => console.warn(...a),   // keep warnings visible
    error: (...a) => console.error(...a)   // keep errors visible
  };

  // ================
  // Simple caches
  // ================
  const productCache = new Map(); // key -> { tags: [], ok: boolean }
  let FEE_VARIANT_ID = null;      // default variant id of tariff-fee (filled on first sync)

  // ---------------------------------------------------
  // Section detection + header id helper (for repaint)
  // ---------------------------------------------------
  function cspDetectCartSectionsOnce() {
    if (window.CSP?.cartSections?.length) return window.CSP.cartSections;

    const found = new Set();
    const wrappers = document.querySelectorAll('[id^="shopify-section-"]');

    const signals = [
      'drawer-modal#HeaderMiniCart',    // Athens drawer
      '.mini-cart-wrap',                // drawer content
      '#header-mini-cart-content',      // drawer body
      '#header-mini-cart-footer',       // drawer footer
      '.head-slot-cart-link-quantity',  // header bubble
      '#main-cart-items',               // cart page
      '[data-cart-items]'               // generic cart items
    ];

    wrappers.forEach(sec => {
      for (const sel of signals) {
        if (sec.querySelector(sel)) {
          found.add(sec.id.replace('shopify-section-', ''));
          break;
        }
      }
    });

    // Fallback anchors -> nearest section
    ['#header-mini-cart-content', '#header-mini-cart-footer', 'mini-cart', 'drawer-modal#HeaderMiniCart', '.head-slot-cart-link-quantity', '#main-cart-items']
      .map(sel => document.querySelector(sel))
      .filter(Boolean)
      .forEach(node => {
        const sec = node.closest('[id^="shopify-section-"]');
        if (sec) found.add(sec.id.replace('shopify-section-', ''));
      });

    const ids = Array.from(found);
    window.CSP = window.CSP || {};
    if (ids.length) window.CSP.cartSections = ids;

    const headerId =
      ids.find(id => /__header$/.test(id)) ||
      Array.from(document.querySelectorAll('[id^="shopify-section-"]'))
        .map(n => n.id.replace('shopify-section-', ''))
        .find(id => /__header$/.test(id)) || null;

    if (headerId) window.CSP.headerSectionId = headerId;
    return ids;
  }

  function getCartSections() {
    return (window.CSP && window.CSP.cartSections && window.CSP.cartSections.length)
      ? window.CSP.cartSections
      : cspDetectCartSectionsOnce();
  }

  function getHeaderSectionId() {
    if (window.CSP?.headerSectionId) return window.CSP.headerSectionId;
    cspDetectCartSectionsOnce();
    return window.CSP?.headerSectionId || null;
  }

  // ================
  // Utility helpers
  // ================
  const getJSON = (url) => fetch(url, { credentials: 'same-origin' }).then(r => r.json());
  const getCart = () => getJSON('/cart.js');

  const updateCart = (updates) =>
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

  const debounce = (fn, wait = 200) => {
    let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  async function fetchProductMetaForLine(li) {
    const cacheKey = li.product_id ? `id:${li.product_id}` : (li.handle ? `h:${li.handle}` : null);
    if (cacheKey && productCache.has(cacheKey)) return productCache.get(cacheKey);

    const lowerTags = (p) =>
      Array.isArray(p?.tags) ? p.tags.map(t => String(t).trim().toLowerCase()) : [];

    const attempts = [];
    if (li.handle) attempts.push(`/products/${li.handle}.js`);
    if (li.url) {
      const base = li.url.replace(/\?.*$/, '').replace(/\/+$/, '');
      if (base) attempts.push(`${base}.js`);
    }
    if (li.product_id) attempts.push(`/products/${li.product_id}.js`);

    for (const path of attempts) {
      try {
        const p = await getJSON(path);
        const data = { tags: lowerTags(p), ok: true };
        if (cacheKey) productCache.set(cacheKey, data);
        if (li.handle && !productCache.has(`h:${li.handle}`)) {
          productCache.set(`h:${li.handle}`, data);
        }
        return data;
      } catch { /* keep trying */ }
    }

    const miss = { tags: [], ok: false };
    if (cacheKey) productCache.set(cacheKey, miss);
    return miss;
  }

  // Drawer helpers
  function isDrawerOpen() {
    return (
      document.body.classList.contains('header-mini-cart-open') ||
      !!document.querySelector('#HeaderMiniCart[open], .drawer.is-open, .drawer.open')
    );
  }

  let __cspDrawerNudged = false;
  async function softNudgeDrawer() {
    if (__cspDrawerNudged) return false;
    __cspDrawerNudged = true;

    const details = document.getElementById('HeaderMiniCart');
    if (details && 'open' in details) {
      try {
        details.removeAttribute('open');
        document.body.classList.remove('header-mini-cart-open');
        void details.offsetWidth; // reflow
        details.setAttribute('open', '');
        document.body.classList.add('header-mini-cart-open');
        return true;
      } catch {}
    }

    const toggler = document.querySelector(
      '[href="#HeaderMiniCart"], [data-action="open-mini-cart"], .head-slot-cart-link, .header-cart-toggle, a[href*="mini-cart"]'
    );
    if (toggler) { toggler.click(); return true; }
    return false;
  }

  async function repaintDrawerFromHeader() {
    const headerId = getHeaderSectionId();
    if (!headerId) return false;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', headerId);
      const htmlById = await getJSON(url.toString());
      const fragHTML = htmlById && htmlById[headerId];
      if (!fragHTML) return false;

      const parser = new DOMParser();
      const doc = parser.parseFromString(fragHTML, 'text/html');

      const newBody   = doc.querySelector('#header-mini-cart-content');
      const newFooter = doc.querySelector('#header-mini-cart-footer');

      const liveBody   = document.querySelector('#header-mini-cart-content');
      const liveFooter = document.querySelector('#header-mini-cart-footer');

      let did = false;
      if (newBody && liveBody) { liveBody.innerHTML = newBody.innerHTML; did = true; }
      if (newFooter && liveFooter) { liveFooter.innerHTML = newFooter.innerHTML; did = true; }

      if (did) {
        document.dispatchEvent(new CustomEvent('cart:refreshed'));
        hardenTariffUI();
      }
      return did;
    } catch { return false; }
  }

  async function refreshCartSections() {
    if (window.CSP) window.CSP.cartSections = [];

    const sectionIds = getCartSections();
    if (!sectionIds.length) {
      await repaintDrawerFromHeader();
      return;
    }

    let replacedAny = false;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      const htmlById = await getJSON(url.toString());

      sectionIds.forEach(id => {
        const container =
          document.querySelector(`[data-section-id="${id}"]`) ||
          document.getElementById(`shopify-section-${id}`);
        if (container && htmlById[id]) {
          container.innerHTML = htmlById[id];
          replacedAny = true;
        }
      });

      document.dispatchEvent(new CustomEvent('cart:refreshed'));
      hardenTariffUI();
    } catch (e) {
      const ok = await repaintDrawerFromHeader();
      if (!ok) {
        console.warn('CSP: Section refresh failed; reloading', e);
        window.location.reload();
      }
      return;
    }

    if (isDrawerOpen()) await repaintDrawerFromHeader();
    if (!replacedAny) await repaintDrawerFromHeader();
  }

  function setCheckoutDisabled(disabled) {
    document.querySelectorAll('button[name="checkout"]').forEach(btn => {
      if (disabled) {
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
      } else {
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      }
    });
  }

  function openDrawerNow() {
    const details = document.getElementById('HeaderMiniCart');
    if (details && 'open' in details) {
      details.setAttribute('open', '');
      document.body.classList.add('header-mini-cart-open');
      return true;
    }
    const toggler = document.querySelector(
      '[href="#HeaderMiniCart"], [data-action="open-mini-cart"], .head-slot-cart-link, .header-cart-toggle, a[href*="mini-cart"]'
    );
    if (toggler) { toggler.click(); return true; }
    return false;
  }

  function maybeReopenDrawerAfterReload() {
    try {
      if (sessionStorage.getItem('CSP_REOPEN_DRAWER') === '1') {
        sessionStorage.removeItem('CSP_REOPEN_DRAWER');
        setTimeout(() => openDrawerNow(), 120);
      }
    } catch {}
  }

  // ==============================
  // Drawer qty display nudger
  // ==============================
  function fixFeeQtyDisplay(expected) {
    if (!FEE_VARIANT_ID || typeof expected !== 'number') return;

    const feeNodes = document.querySelectorAll(
      `[data-wpd-cart-variant-id="${FEE_VARIANT_ID}"], [data-wpd-cart-drawer-variant-id="${FEE_VARIANT_ID}"]`
    );

    feeNodes.forEach(node => {
      const root = node.closest('.mini-cart-item, .js-cart-item, tr, [data-cart-items] *');
      if (!root) return;

      const input = root.querySelector('input.quantity-input-field');
      if (input && Number(input.value) !== expected) {
        input.value = expected;
        input.setAttribute('value', String(expected));
      }

      const wpdQtyNode = root.querySelector('[data-wpd-cart-drawer-quantity]');
      if (wpdQtyNode) {
        wpdQtyNode.setAttribute('data-wpd-cart-drawer-quantity', String(expected));
      }

      const qtyText = root.querySelector('.mini-cart-item-qty, .cart-item-qty');
      if (qtyText && qtyText.textContent.trim() !== String(expected)) {
        qtyText.textContent = String(expected);
      }
    });
  }

  // ==========================================
  // Prune visual ghosts of the fee line (UI)
  // ==========================================
  async function pruneTariffVisualGhosts(currentCart) {
    if (!FEE_VARIANT_ID) return;

    const cart = currentCart || await getCart();
    const feeExists = cart.items?.some(i => i.id === FEE_VARIANT_ID) || false;

    const nodes = document.querySelectorAll(
      `[data-wpd-cart-variant-id="${FEE_VARIANT_ID}"], [data-wpd-cart-drawer-variant-id="${FEE_VARIANT_ID}"]`
    );

    nodes.forEach(node => {
      const root = node.closest('.mini-cart-item, .js-cart-item, tr, [data-cart-items] *');
      if (!root) return;

      let displayedQty = 0;
      const input = root.querySelector('input.quantity-input-field');
      if (input) displayedQty = Number(input.value) || 0;

      const wpdQtyNode = root.querySelector('[data-wpd-cart-drawer-quantity]');
      if (wpdQtyNode) {
        const n = Number(wpdQtyNode.getAttribute('data-wpd-cart-drawer-quantity'));
        if (!Number.isNaN(n)) displayedQty = n;
      }

      if (!feeExists || displayedQty === 0) {
        root.remove();
      }
    });
  }

  // ==============================
  // UI hardening for the fee line
  // ==============================
  function hardenTariffUI() {
    try {
      if (!FEE_VARIANT_ID) return;

      // Drawer (Athens) + generic
      const drawerVariantNodes = document.querySelectorAll(
        `[data-wpd-cart-variant-id="${FEE_VARIANT_ID}"], [data-wpd-cart-drawer-variant-id="${FEE_VARIANT_ID}"]`
      );

      drawerVariantNodes.forEach(node => {
        const drawerItem = node.closest('.mini-cart-item, .js-cart-item, [data-cart-items] *');
        if (!drawerItem) return;

        const removeLink = drawerItem.querySelector('a.mini-cart-item-remove, cart-remove-button a, .button-cart-remove');
        if (removeLink) {
          removeLink.style.display = 'none';
          removeLink.addEventListener('click', e => e.preventDefault(), { once: true });
        }

        const qtyInput = drawerItem.querySelector('quantity-input input[type="number"], .quantity-input-field');
        if (qtyInput) {
          qtyInput.readOnly = true;
          qtyInput.setAttribute('aria-readonly', 'true');
          qtyInput.style.pointerEvents = 'none';
        }

        const qtyMinus = drawerItem.querySelector('.quantity-input-minus');
        const qtyPlus  = drawerItem.querySelector('.quantity-input-plus');
        [qtyMinus, qtyPlus].forEach(btn => { if (btn) { btn.disabled = true; btn.style.pointerEvents = 'none'; } });
      });

      // Cart page
      const pageVariantNodes = document.querySelectorAll(
        `#main-cart-items [data-wpd-cart-variant-id="${FEE_VARIANT_ID}"]`
      );

      pageVariantNodes.forEach(node => {
        const row = node.closest('.cart-item, .js-cart-item, tr');
        if (!row) return;

        const removeLink = row.querySelector('a.button-cart-remove, cart-remove-button a, .mini-cart-item-remove');
        if (removeLink) {
          removeLink.style.display = 'none';
          removeLink.addEventListener('click', e => e.preventDefault(), { once: true });
        }

        const qtyInput = row.querySelector('quantity-input input[type="number"], .quantity-input-field');
        if (qtyInput) {
          qtyInput.readOnly = true;
          qtyInput.setAttribute('aria-readonly', 'true');
          qtyInput.style.pointerEvents = 'none';
        }

        const qtyMinus = row.querySelector('.quantity-input-minus');
        const qtyPlus  = row.querySelector('.quantity-input-plus');
        [qtyMinus, qtyPlus].forEach(btn => { if (btn) { btn.disabled = true; btn.style.pointerEvents = 'none'; } });
      });

      // Intercept any crafted remove links
      document.querySelectorAll(`a[href*="/cart/change"]`).forEach(a => {
        if (a.href.includes(`id=${FEE_VARIANT_ID}:`) || a.href.includes(`id=${FEE_VARIANT_ID}&`)) {
          a.style.display = 'none';
          a.addEventListener('click', e => e.preventDefault(), { once: true });
        }
      });
    } catch (e) {
      console.warn('CSP: hardenTariffUI error', e);
    }
  }

  // ===================================
  // Main: keep tariff fee line in sync
  // ===================================
  let syncing = false;

  const syncTariffFees = async () => {
    if (syncing) return;
    syncing = true;

    try {
      const cart = await getCart();
      const items = cart?.items || [];

      // Separate fee line and normal lines (do this BEFORE using feeLine)
      let feeLine = null;
      const normalLines = [];
      for (const li of items) {
        if (li.handle === TARIFF_FEE_HANDLE) {
          feeLine = { id: li.id, qty: li.quantity };
        } else {
          normalLines.push(li);
        }
      }

      // Count eligible qty (use robust fetch + memo)
      let eligibleQty = 0;
      const results = await Promise.all(
        normalLines.map(async li => ({ li, data: await fetchProductMetaForLine(li) }))
      );

      // Debug visibility if needed:
      // results.forEach(({ li, data }) => CSP_LOG.debug('[CSP] count?', li.handle, { qty: li.quantity, ok: data.ok, tags: data.tags }));

      for (const { li, data } of results) {
        if (data?.ok && data.tags.includes(ELIGIBLE_TAG) && li.quantity > 0) {
          eligibleQty += li.quantity;
        }
      }

      // Ensure fee variant id
      if (!FEE_VARIANT_ID) {
        try {
          const feeProduct = await getJSON(`/products/${TARIFF_FEE_HANDLE}.js`);
          if (feeProduct?.variants?.length) {
            FEE_VARIANT_ID = feeProduct.variants[0].id;
          }
        } catch (e) {
          console.warn('CSP: could not load tariff-fee product JSON', e);
        }
      }

      // Build updates
      const updates = {};
      if (FEE_VARIANT_ID) {
        if (eligibleQty > 0) {
          const currentQty = feeLine?.qty || 0;
          if (currentQty !== eligibleQty) updates[FEE_VARIANT_ID] = eligibleQty;
        } else {
          if (feeLine?.id) updates[feeLine.id] = 0;
        }
      }

      if (Object.keys(updates).length) {
        setCheckoutDisabled(true);

        const prevFeeQty = feeLine?.qty || 0;
        const nextFeeQty = updates[FEE_VARIANT_ID] ?? prevFeeQty;
        const weTouchedFee =
          typeof updates[FEE_VARIANT_ID] !== 'undefined' && nextFeeQty !== prevFeeQty;

        await updateCart(updates);

        // First repaint → fix numbers → prune ghosts
        await refreshCartSections();
        fixFeeQtyDisplay(eligibleQty);
        await pruneTariffVisualGhosts(cart); // we already have the cart here

        // Let any theme/app listeners react
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Follow-up repaint (drawer-first race)
        if (weTouchedFee && isDrawerOpen()) {
          setTimeout(async () => {
            await refreshCartSections();
            fixFeeQtyDisplay(eligibleQty);
            document.dispatchEvent(new CustomEvent('cart:updated'));

            const feeVisible = !!document.querySelector(
              `[data-wpd-cart-variant-id="${FEE_VARIANT_ID}"], [data-wpd-cart-drawer-variant-id="${FEE_VARIANT_ID}"]`
            );

            if (!feeVisible) {
              const nudged = await softNudgeDrawer();
              if (!nudged) hardenTariffUI();

              const stillMissing = !document.querySelector(
                `[data-wpd-cart-variant-id="${FEE_VARIANT_ID}"], [data-wpd-cart-drawer-variant-id="${FEE_VARIANT_ID}"]`
              );
              if (stillMissing) {
                try { sessionStorage.setItem('CSP_REOPEN_DRAWER', '1'); } catch {}
                window.location.reload();
              }
            }
          }, 350);
        } else {
          // No fee qty change or drawer closed: just harden + ensure display is right
          hardenTariffUI();
          if (eligibleQty > 0) {
            fixFeeQtyDisplay(eligibleQty);
          } else {
            await pruneTariffVisualGhosts(cart);
          }
        }
      } else {
        // No updates: harden and fix/prune based on current eligible qty
        hardenTariffUI();
        if (eligibleQty > 0) {
          fixFeeQtyDisplay(eligibleQty);
        } else {
          await pruneTariffVisualGhosts(cart);
        }
      }
    } catch (e) {
      console.error('CSP tariff fee sync error:', e);
    } finally {
      syncing = false;
      setCheckoutDisabled(false);
    }
  };

  // ==================================================
  // Hook into AJAX cart activity so we sync "on the fly"
  // ==================================================
  const debouncedSync = debounce(() => syncTariffFees(), 150);

  // Patch fetch to detect cart mutations
  if (!window.__cspPatchedFetch) {
    const origFetch = window.fetch;
    window.fetch = async function(input, init = {}) {
      const res = await origFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = (init?.method || 'GET').toUpperCase();
        if (method === 'POST' && /\/cart\/(add|change|update)\.js/.test(url)) {
          debouncedSync();
        }
      } catch {}
      return res;
    };
    window.__cspPatchedFetch = true;
  }

  document.addEventListener('cart:updated', debouncedSync);
  document.addEventListener('cart:change', debouncedSync);
  document.addEventListener('product:added', debouncedSync);

  // When a section repaint completes, do a quick prune (it will fetch cart if needed)
  document.addEventListener('cart:refreshed', () => {
    pruneTariffVisualGhosts();
  });

  // Mutation observers around cart UI
  let __cspCartObservers = [];
  function cartObserverTargets() {
    return [
      document.getElementById('HeaderMiniCart'),
      document.querySelector('.mini-cart-wrap'),
      document.getElementById('header-mini-cart-content'),
      document.getElementById('header-mini-cart-footer'),
      document.querySelector('.head-slot-cart-link-quantity'),
      document.getElementById('main-cart-items')
    ].filter(Boolean);
  }
  function attachCartObservers() {
    __cspCartObservers.forEach(o => o.disconnect());
    __cspCartObservers = [];
    const cb = debounce(() => debouncedSync(), 100);
    cartObserverTargets().forEach(root => {
      const obs = new MutationObserver(cb);
      obs.observe(root, { childList: true, subtree: true });
      __cspCartObservers.push(obs);
    });
  }
  attachCartObservers();
  document.addEventListener('cart:refreshed', () => {
    attachCartObservers();
    hardenTariffUI();
  });

  // Pre-checkout enforcement
  function interceptCheckoutButtons() {
    const clickHandler = async (e) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        await syncTariffFees();
        await new Promise(r => setTimeout(r, 150));
        const btn = e.currentTarget;
        const form = btn.closest('form');
        if (form && form.action && /\/cart(?:\/.*)?$/.test(form.action)) {
          form.submit();
        } else {
          btn.removeEventListener('click', clickHandler);
          btn.click();
        }
      } catch (_) {
        const btn = e.currentTarget;
        btn.removeEventListener('click', clickHandler);
        btn.click();
      }
    };

    const candidates = [
      'form[action="/cart"] button[name="checkout"]',
      '#header-mini-cart-footer button[name="checkout"]',
      '.mini-cart-footer button[name="checkout"]'
    ];

    candidates.forEach(sel => {
      document.querySelectorAll(sel).forEach(btn => {
        if (!btn.dataset.cspCheckoutBound) {
          btn.dataset.cspCheckoutBound = '1';
          btn.addEventListener('click', clickHandler);
        }
      });
    });
  }

  // Checkout safety latch
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest('button[name="checkout"]');
    if (!btn) return;

    if (syncing) {
      e.preventDefault();
      const go = () => { window.location.href = '/checkout'; };
      setTimeout(go, 250);
      return;
    }

    setTimeout(() => {
      const stillOpen = document.body.classList.contains('header-mini-cart-open');
      if (stillOpen) window.location.href = '/checkout';
    }, 400);
  }, { capture: true });

  // Init
  document.addEventListener('DOMContentLoaded', () => {
    maybeReopenDrawerAfterReload();
    debouncedSync();
    hardenTariffUI();
    interceptCheckoutButtons();
  });
  document.addEventListener('cart:refreshed', interceptCheckoutButtons);
})();
